import { ArrowLeftOutlined, DownloadOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Empty, message, Skeleton, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { queryErrorMessage } from '../CashTransactions/types';
import AppIcon from './AppIcon';
import MobileAppFormDrawer from './MobileAppFormDrawer';
import type { MobileAppVersion, MobileAppVersionsResult } from './types';

const { Title, Paragraph, Text } = Typography;

// One Mobile app and its versions, newest first. Versions are added by the
// build pipeline (POST /custom/mobile-app/version), not here.
export default function MobileAppVersions({ appId }: { appId: number }) {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';
  const navigate = useNavigate();
  const { appKey } = useParams<{ appKey: string }>();
  const [result, setResult] = useState<MobileAppVersionsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<MobileAppVersionsResult>('mobile_app_versions', { appId })
      .then(([row]) => setResult(row ?? null))
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, appId]);

  useEffect(() => {
    load();
  }, [load]);

  const app = result?.app;
  const versions = result?.versions ?? [];
  const latest = versions[0];

  const columns: ColumnsType<MobileAppVersion> = [
    {
      title: 'Хувилбар',
      dataIndex: 'version',
      width: 200,
      render: (value: string, _row, index) => (
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2.5 py-0.5 font-mono text-sm font-semibold ${
              index === 0 ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'
            }`}
          >
            v{value}
          </span>
          {index === 0 && <span className="text-xs font-medium text-green-600">Сүүлийн</span>}
        </div>
      ),
    },
    {
      title: 'Өөрчлөлт',
      dataIndex: 'release_notes',
      render: (value: string | null) =>
        value ? (
          <Paragraph
            className="mb-0! whitespace-pre-line text-gray-700"
            ellipsis={{
              rows: 3,
              expandable: 'collapsible',
              symbol: (open) => (open ? 'Хураах' : 'Дэлгэрэнгүй'),
            }}
          >
            {value}
          </Paragraph>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Оруулсан огноо',
      dataIndex: 'uploaded_at',
      width: 180,
      render: (value: string | null) =>
        value ? (
          <div className="leading-tight">
            <div className="text-gray-700">{dayjs(value).format('YYYY.MM.DD')}</div>
            <div className="text-xs text-gray-400">{dayjs(value).format('HH:mm')}</div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'APK',
      dataIndex: 'apk_url',
      width: 120,
      render: (value: string | null) =>
        value ? (
          <Button type="link" icon={<DownloadOutlined />} href={value} className="px-0!">
            Татах
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        className="mb-2 px-0!"
        onClick={() => navigate(`/apps/${appKey}/mobile_apps`)}
      >
        Мобайл апп
      </Button>

      <Card className="mb-4">
        {!app ? (
          <Skeleton avatar={{ shape: 'square', size: 72 }} active paragraph={{ rows: 2 }} />
        ) : (
          <div className="flex flex-col gap-5 md:flex-row md:items-start">
            <div className="shrink-0 self-start rounded-2xl shadow-sm ring-1 ring-black/5">
              <AppIcon url={app.icon_url} size={72} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Title level={3} style={{ margin: 0 }}>
                  {app.name}
                </Title>
                <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-500">
                  {app.code}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <Stat label="Хувилбар" value={String(versions.length)} />
                <Stat label="Сүүлийн" value={latest ? `v${latest.version}` : '—'} />
                <Stat
                  label="Шинэчилсэн"
                  value={latest?.uploaded_at ? dayjs(latest.uploaded_at).format('YYYY.MM.DD') : '—'}
                />
              </div>

              {app.description && (
                <Paragraph type="secondary" className="mt-3! mb-0! max-w-3xl whitespace-pre-line">
                  {app.description}
                </Paragraph>
              )}
            </div>

            <div className="flex shrink-0 gap-2">
              <Tooltip title="Шинэчлэх">
                <Button icon={<ReloadOutlined />} loading={loading} onClick={load} />
              </Tooltip>
              {canEdit && (
                <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                  Засах
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card title="Хувилбарууд">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={versions}
          loading={loading && result !== null}
          scroll={{ x: true }}
          locale={{
            emptyText: app ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="py-6"
                description="Хувилбар нэмэгдээгүй байна"
              />
            ) : (
              ' '
            ),
          }}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      </Card>

      <MobileAppFormDrawer
        open={editing}
        app={app ?? null}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          load();
        }}
      />
    </LayoutWithMenu>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-gray-400">
      {label}: <span className="font-medium text-gray-700">{value}</span>
    </span>
  );
}
